import { useCallback, useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { DataTable } from '@duke-hq/ui/components/DataTable/index';
import { useForm } from 'react-hook-form';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import type {
  MigrationInput,
  MigrationInputField,
} from '@duke-hq/svc-api/admin/trpc/routers/migrations/types';
import { DataTableCellAction, DataTableSchema } from '@duke-hq/ui/components/DataTable/schema';
import { MigrationRuns } from './MigrationRuns';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  FormControl,
  Input,
  Stack,
  Button,
  Divider,
  Text,
  Textarea,
  Checkbox,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { SortOrder } from '@duke-hq/lib/pagination';
import { withPermission } from '../withPermission';

export const DataMigrationsPage = withPermission('canAccessDeveloperTools', () => {
  return <DataMigrationsTable />;
});

export function DataMigrationsTable() {
  const [sortOrder, setSortOrder] = useState<{
    [key: string]: SortOrder | undefined;
  }>({});
  const [runProc, setRunProc] = useState<
    { procedure: string; staticForm?: MigrationInput } | undefined
  >(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  // Not the mutation's isPending: that locks an unrelated migration's modal.
  const [runningProcedure, setRunningProcedure] = useState<string | undefined>(undefined);
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const { selectedProcedureName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const onCloseDrawer = useCallback(() => {
    navigate(`/developer-tools/data-migrations/?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  const { data: migrationsData, refetch } = trpcAdmin.migrations.list.useQuery({
    table: {
      sortOrder,
    },
    search: searchQuery.trim() ?? undefined,
  });

  // One column at a time: the table draws no multi-sort affordance.
  const onSortChange = useCallback(
    (columnName: string, direction?: SortOrder) =>
      setSortOrder(direction === undefined ? {} : { [columnName]: direction }),
    [setSortOrder],
  );

  const runMigration = trpcAdmin.migrations.run.useMutation({
    // Settled, not success: a failure writes a FAILURE row too.
    onSettled: () => {
      void refetch();
    },
  });

  const runWithFeedback = useCallback(
    async (procedure: string, data?: Record<string, unknown>): Promise<boolean> => {
      const toastId = toast({
        title: procedure,
        description: 'Running migration…',
        status: 'loading',
        duration: null,
      });

      setRunningProcedure(procedure);
      try {
        await runMigration.mutateAsync({ procedure, data });
        toast.update(toastId, {
          title: 'Success',
          description: `${procedure} completed successfully`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        return true;
      } catch (error) {
        toast.update(toastId, {
          title: 'Error',
          description: error instanceof Error ? error.message : `${procedure} failed`,
          status: 'error',
          duration: 10000,
          isClosable: true,
        });
        return false;
      } finally {
        setRunningProcedure(undefined);
      }
    },
    [runMigration, toast],
  );

  const handleAction = useCallback(
    async (
      action: DataTableCellAction<{
        procedure: string;
        form?: MigrationInput;
        needsFormFetch?: boolean;
      }>,
    ) => {
      switch (action.kind) {
        case 'run.migration': {
          if (
            action.data !== null &&
            'procedure' in action.data &&
            typeof action.data['procedure'] === 'string'
          ) {
            const { procedure, form, needsFormFetch } = action.data;
            if (form !== undefined) {
              setRunProc({ procedure, staticForm: form });
            } else if (needsFormFetch === true) {
              void utils.migrations.getRunForm.prefetch({ procedure });
              setRunProc({ procedure });
            } else {
              await runWithFeedback(procedure);
            }
          }
          break;
        }
        case 'view.runs': {
          if (
            action.data !== null &&
            'procedure' in action.data &&
            typeof action.data['procedure'] === 'string'
          ) {
            navigate(
              `/developer-tools/data-migrations/${action.data.procedure}?${searchParams.toString()}`,
            );
          }
          break;
        }
      }
    },
    [navigate, runWithFeedback, searchParams, utils],
  );

  const isRunProcRunning = typeof runProc !== 'undefined' && runningProcedure === runProc.procedure;

  const data = useMemo(() => {
    if (typeof migrationsData === 'undefined') {
      return;
    }

    return {
      headers: {
        ...migrationsData?.headers,
        view: {
          label: 'View',
          sortable: false,
        },
      },
      rows: migrationsData.rows.map((row) => ({
        id: row.id,
        cells: {
          ...row.cells,
          view: {
            type: 'action',
            label: 'View',
            kind: 'view.runs',
            data: {
              procedure: row.cells.name.value,
            },
            size: 'sm',
            variant: 'outline',
            colorScheme: 'gray',
          },
        },
      })),
    } satisfies DataTableSchema;
  }, [migrationsData]);

  if (typeof migrationsData === 'undefined' || typeof data === 'undefined') {
    return <div>Loading...</div>;
  }

  return (
    <>
      <TableViewLayout
        pageHeader={[
          <PageHeading key="data-migrations">
            <Trans>Data Migrations</Trans>
          </PageHeading>,
        ]}
        filters={
          <SearchInput
            maxW="search-input"
            onCommit={setSearchQuery}
            placeholder="Search migrations"
          />
        }
        tableActionsLeft={null}
        table={
          <DataTable
            data={data}
            onSortChange={onSortChange}
            handleAction={handleAction}
            tableProps={{
              colorScheme: 'purple',
              size: 'sm',
            }}
          />
        }
        pagination={null}
      />
      <MigrationRuns
        procedureName={selectedProcedureName}
        isOpen={typeof selectedProcedureName === 'string'}
        onClose={onCloseDrawer}
      />
      <FormModalWrapper
        modalContentHeight="auto"
        isOpen={typeof runProc !== 'undefined'}
        modalCloseButton={!isRunProcRunning}
        onClose={() => {
          if (!isRunProcRunning) {
            setRunProc(undefined);
          }
        }}
        modalContent={
          typeof runProc !== 'undefined' ? (
            <RunMigrationModalContent
              procedure={runProc.procedure}
              staticForm={runProc.staticForm}
              isRunning={isRunProcRunning}
              onSubmit={async (data) => {
                if (await runWithFeedback(runProc.procedure, data)) {
                  setRunProc(undefined);
                }
              }}
            />
          ) : null
        }
      />
    </>
  );
}

function RunMigrationModalContent({
  procedure,
  staticForm,
  isRunning,
  onSubmit,
}: {
  procedure: string;
  staticForm: MigrationInput | undefined;
  isRunning: boolean;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
}) {
  const {
    data: fetchedForm,
    error,
    refetch,
    isFetching,
  } = trpcAdmin.migrations.getRunForm.useQuery(
    { procedure },
    { enabled: staticForm === undefined, retry: false },
  );

  const form = staticForm ?? fetchedForm;

  if (form !== undefined) {
    return (
      <InputForm fields={form} procedure={procedure} isRunning={isRunning} onSubmit={onSubmit} />
    );
  }

  return (
    <Stack style={{ width: '100%', padding: '1rem' }} spacing={4}>
      <Text fontSize="lg" fontFamily="monospace" fontWeight="bold">
        {procedure}
      </Text>
      <Divider />
      {error ? (
        <Stack spacing={4}>
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
          <Button
            onClick={() => {
              void refetch();
            }}
            isLoading={isFetching}
            colorScheme="purple"
            alignSelf="flex-start"
            size="sm"
          >
            Retry
          </Button>
        </Stack>
      ) : (
        <Stack align="center" py={8} spacing={3}>
          <Spinner color="purple.500" size="lg" />
          <Text fontSize="sm" color="fg.muted">
            Loading form…
          </Text>
        </Stack>
      )}
    </Stack>
  );
}

function InputForm({
  onSubmit,
  procedure,
  fields,
  isRunning,
}: {
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  procedure: string;
  fields: MigrationInput;
  isRunning: boolean;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<Record<string, string | string[] | boolean>>();

  const handleSelectAll = useCallback(
    (
      fieldKey: string,
      options: (string | { label: string; value: string })[],
      isChecked: boolean,
    ) => {
      if (isChecked) {
        const allValues = options.map((option) =>
          typeof option === 'string' ? option : option.value,
        );
        setValue(fieldKey, allValues, { shouldValidate: true });
      } else {
        setValue(fieldKey, [] satisfies string[], { shouldValidate: true });
      }
    },
    [setValue],
  );

  const handleCheckboxChange = useCallback(
    (fieldKey: string, value: string, isChecked: boolean) => {
      const watchedValue = watch(fieldKey);
      const currentValues = Array.isArray(watchedValue) ? watchedValue : [];
      if (isChecked) {
        setValue(fieldKey, [...currentValues, value], { shouldValidate: true });
      } else {
        setValue(
          fieldKey,
          currentValues.filter((v) => v !== value),
          { shouldValidate: true },
        );
      }
    },
    [watch, setValue],
  );

  return (
    <form style={{ width: '100%', padding: '1rem' }} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Text mb={4} fontSize="lg" fontFamily="monospace" fontWeight="bold">
        {procedure}
      </Text>
      <Stack spacing={4}>
        <Divider />
        {Object.entries(fields).map(([key, maybeField]) => {
          const field = isMigrationInputField(maybeField) ? maybeField : null;
          if (field === null) {
            return null;
          }

          const watchedValues = watch(key) || [];
          const watchedValuesArray =
            field.type === 'checkbox' && Array.isArray(watchedValues) ? watchedValues : [];

          const allOptionValues =
            field.type === 'checkbox'
              ? field.options.map((option) => (typeof option === 'string' ? option : option.value))
              : [];
          const isAllSelected =
            field.type === 'checkbox'
              ? allOptionValues.length > 0 &&
                allOptionValues.every((value) => watchedValuesArray.includes(value))
              : false;
          const isIndeterminate =
            field.type === 'checkbox'
              ? watchedValuesArray.length > 0 && watchedValuesArray.length < allOptionValues.length
              : false;

          switch (field.type) {
            case 'checkbox': {
              return (
                <FormControl
                  key={key}
                  isInvalid={!!errors[key]}
                  isRequired={field.required === true}
                >
                  <FormLabel>{field.label ?? ''}</FormLabel>
                  <FormHelperText>{field.description ?? ''}</FormHelperText>
                  <Stack mt={4}>
                    <Checkbox
                      isChecked={isAllSelected}
                      isIndeterminate={isIndeterminate}
                      onChange={(e) => handleSelectAll(key, field.options, e.target.checked)}
                      fontWeight="semibold"
                      borderColor="purple.300"
                    >
                      Select all ({field.options.length})
                    </Checkbox>
                    <Divider />
                    <Box maxH="20rem" overflowY="auto" pr={2}>
                      <Stack>
                        {field.options.map((option) => {
                          const optionValue = typeof option === 'string' ? option : option.value;
                          const optionLabel = typeof option === 'string' ? option : option.label;
                          return (
                            <Checkbox
                              key={optionValue}
                              value={optionValue}
                              isChecked={watchedValuesArray.includes(optionValue)}
                              onChange={(e) =>
                                handleCheckboxChange(key, optionValue, e.target.checked)
                              }
                              pl={4}
                            >
                              {optionLabel}
                            </Checkbox>
                          );
                        })}
                      </Stack>
                    </Box>
                    {/* Hidden input for validation */}
                    <input
                      type="hidden"
                      {...register(key, {
                        required: field.required === true ? 'This field is required' : false,
                        validate: (value) => {
                          if (
                            field.required === true &&
                            (value == null || (Array.isArray(value) && value.length === 0))
                          ) {
                            return 'This field is required';
                          }
                          return true;
                        },
                      })}
                    />
                  </Stack>
                  <FormErrorMessage>{errors[key]?.message}</FormErrorMessage>
                </FormControl>
              );
            }
            case 'text':
              return (
                <FormControl
                  key={key}
                  isInvalid={!!errors[key]}
                  isRequired={field.required === true}
                >
                  <FormLabel>{field.label ?? ''}</FormLabel>
                  <Input
                    id={key}
                    defaultValue={field.defaultValue}
                    {...register(key, {
                      required: field.required === true ? 'This field is required' : false,
                    })}
                  />
                  <FormHelperText>{field.description ?? ''}</FormHelperText>
                  <FormErrorMessage>{errors[key]?.message}</FormErrorMessage>
                </FormControl>
              );
            case 'textarea':
              return (
                <FormControl
                  key={key}
                  isInvalid={!!errors[key]}
                  isRequired={field.required === true}
                >
                  <FormLabel>{field.label ?? ''}</FormLabel>
                  <Textarea
                    id={key}
                    defaultValue={field.defaultValue}
                    {...register(key, {
                      required: field.required === true ? 'This field is required' : false,
                    })}
                  />
                  <FormHelperText>{field.description ?? ''}</FormHelperText>
                  <FormErrorMessage>{errors[key]?.message}</FormErrorMessage>
                </FormControl>
              );
            case 'boolean':
              return (
                <FormControl
                  key={key}
                  isInvalid={!!errors[key]}
                  isRequired={field.required === true}
                >
                  <Checkbox
                    id={key}
                    defaultChecked={field.defaultValue === true}
                    {...register(key, {
                      required: field.required === true ? 'This field is required' : false,
                    })}
                  >
                    {field.label}
                  </Checkbox>
                  <FormHelperText>{field.description ?? ''}</FormHelperText>
                  <FormErrorMessage>{errors[key]?.message}</FormErrorMessage>
                </FormControl>
              );
            case 'radio':
              // currently unsupported. If you need this please add it
              return null;
            default:
              return null;
          }
        })}
        <Button
          type="submit"
          colorScheme="purple"
          size="sm"
          isLoading={isRunning}
          loadingText="Running…"
        >
          Submit
        </Button>
      </Stack>
    </form>
  );
}

function isMigrationInputField(input: unknown): input is MigrationInputField {
  return typeof input === 'object' && input !== null && 'type' in input;
}
